/* eslint-disable @typescript-eslint/no-explicit-any */
import { Badge } from "@/components/ui/others/Badges";
import { Button } from "@/components/ui/button";
import { BookOpen, Calendar, DollarSign } from "lucide-react";
import { useDispatch } from "react-redux";
import useQualification from "@/module/education/api/useQualification";
import {
  setCourseDetailId,
  setIsCourseDetails,
  setStartApplication,
} from "@/module/education/stores/educationSlice";

function formatCustomFieldLabel(key: string) {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeCustomFieldValue(value: unknown) {
  if (value == null || value === "") return "";
  if (Array.isArray(value)) return value.filter(Boolean).map(String).join(", ");
  if (typeof value === "object") return "";
  return String(value);
}

function getCustomFieldInfo(course: any) {
  const entries: Array<{ label: string; value: string }> = [];
  const sources = [course?.customInputs, course?.university?.customInputs];

  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    for (const [key, rawValue] of Object.entries(source)) {
      if (key === "tags") continue;
      const label = formatCustomFieldLabel(key);
      const value = normalizeCustomFieldValue(rawValue);
      if (!value) continue;
      if (entries.some((entry) => entry.label === label && entry.value === value)) continue;
      entries.push({ label, value });
    }
  }

  return entries;
}

export default function CourseRow({ course }: any) {
  const dispatch = useDispatch();
  const { levelSelects } = useQualification();
  const customFieldInfo = getCustomFieldInfo(course);
  const visibleCustomFields = customFieldInfo.slice(0, 2);
  const remainingCustomFieldCount = Math.max(
    0,
    customFieldInfo.length - visibleCustomFields.length
  );
  const levelName =
    levelSelects?.find(
      (val: any) =>
        String(val.value) ===
        String(course.qualificationLevelId ?? course.levelId ?? course.level)
    )?.name ?? course?.level;

  const courseApply = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setCourseDetailId(course.id));
    dispatch(setStartApplication(true));
  };

  const courseView = () => {
    dispatch(setCourseDetailId(course.id));
    dispatch(setIsCourseDetails(true));
  };

  return (
    <div
      onClick={courseView}
      className="border border-[var(--color-black-1)] rounded-[12px] p-3 flex flex-col gap-2 cursor-pointer"
      style={{
        background:
          "linear-gradient(175.944deg, rgb(237, 250, 245) 11.475%, rgb(255, 255, 255) 52.352%)",
      }}
    >
      {/* Top Row */}
      <div className="flex w-full items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <p className="text-[14px] font-medium leading-snug tracking-[-0.56px] text-[var(--color-black-10)]">
            {course.name}
          </p>
          <p className="text-[12px] font-normal leading-normal tracking-[-0.24px] text-[var(--color-black-7)]">
            {course.university?.name || ""}
          </p>
        </div>
        {course.matchPercentage && (
          <Badge className="bg-[#c2f5da] text-[#0b4627] py-[2px] px-2 text-[12px] tracking-[-0.24px] font-medium rounded-full shrink-0">
            {course.matchPercentage}% match
          </Badge>
        )}
      </div>

      {visibleCustomFields.length > 0 && (
        <div className="flex w-full flex-wrap gap-2">
          {visibleCustomFields.map((field) => (
            <Badge
              key={`${field.label}-${field.value}`}
              className="bg-[#f2f5f8] text-[#222530] py-1 px-2 text-[12px] tracking-[-0.24px] font-normal rounded-full shrink-0 max-w-full"
            >
              <span className="truncate">
                {field.label}: {field.value}
              </span>
            </Badge>
          ))}
          {remainingCustomFieldCount > 0 && (
            <Badge className="bg-[#f2f5f8] text-[#222530] py-1 px-2 text-[12px] tracking-[-0.24px] font-normal rounded-full shrink-0">
              +{remainingCustomFieldCount} more
            </Badge>
          )}
        </div>
      )}

      {/* Bottom Row */}
      <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2">
          {/* Course Type Badge with icon */}
          <Badge
            className="text-white py-[2px] px-2 text-[12px] tracking-[-0.24px] font-normal rounded-full shrink-0 flex items-center gap-1"
            style={{
              backgroundColor: "#b577ed",
              boxShadow: "inset 0px 4px 4px 0px rgba(218, 249, 237, 0.25)",
            }}
          >
            <BookOpen size={14} />
            {course.courseType}
          </Badge>

          {/* Level Badge */}
          {levelName && (
            <Badge className="bg-[#f2f5f8] text-[#222530] py-1 px-2 text-[12px] tracking-[-0.24px] font-normal rounded-full shrink-0">
              {levelName}
            </Badge>
          )}

          {/* Duration */}
          <div className="flex items-center gap-1.5 px-2 shrink-0">
            <Calendar
              size={18}
              className="text-[var(--color-black-8)]"
              strokeWidth={1.5}
            />
            <span className="text-[14px] tracking-[-0.56px] leading-[1.35] text-[var(--color-black-8)] font-normal">
              {course.duration}- Months
            </span>
          </div>

          {/* Tuition Fee */}
          <div className="flex items-center gap-1.5 px-2 shrink-0">
            <DollarSign
              size={18}
              className="text-[var(--color-black-8)]"
              strokeWidth={1.5}
            />
            <span className="text-[14px] tracking-[-0.56px] leading-[1.35] text-[var(--color-black-8)] font-normal">
              {Number(course.minFee) > 0 ? course.minFee : "—"}
            </span>
          </div>
        </div>

        {/* Apply Button */}
        {/* {course.matchPercentage && ( */}
        <Button
          variant="secondary"
          className="h-9 px-2 py-1.5 rounded-[12px] font-medium text-[14px] tracking-[-0.56px] text-[var(--color-black-9)] shrink-0 border-[var(--color-green-4)] border-[0px_0.5px_0.9px] border-solid"
          onClick={courseApply}
        >
          Apply
        </Button>
        {/* )} */}
      </div>
    </div>
  );
}
